import { useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Navigate, useRouterState } from '@tanstack/react-router'

import { authKeys, useCurrentUserQuery } from '@/api/auth/auth-queries'
import { LOGIN_PATH } from '@/config/auth-routes'
import { accessFor, leafRouteId } from '@/config/navigation'
import { SessionSplash } from '@/features/auth/components/session-splash'
import { onSessionExpired } from '@/features/auth/model/session-events'

interface RequireSessionProps {
  children: ReactNode
}

export function RequireSession({ children }: RequireSessionProps) {
  const queryClient = useQueryClient()
  const query = useCurrentUserQuery()
  const routeId = useRouterState({ select: ({ matches }) => leafRouteId(matches) })
  const href = useRouterState({
    select: ({ location, resolvedLocation }) => (resolvedLocation ?? location).href,
  })

  useEffect(
    () => onSessionExpired(() => queryClient.setQueryData(authKeys.me(), null)),
    [queryClient],
  )

  if (query.data === null) {
    return <Navigate to={LOGIN_PATH} search={{ redirect: href }} replace />
  }

  const access = query.data ? accessFor(query.data, routeId) : undefined
  if (access?.kind === 'elsewhere') {
    return (
      <>
        <SessionSplash />
        <Navigate to={access.to} replace />
      </>
    )
  }

  return <>{children}</>
}
